import { Handle, type NodeProps, Position } from "reactflow";
import type { MapViewNode } from "@/lib/core/schemas/map-view";
import { cn } from "@/lib/utils";

export type LocationNodeData = MapViewNode & { isPartyLocation: boolean };

const MODE_PROFILE_THEME: Record<
  MapViewNode["modeProfile"],
  { icon: string; border: string }
> = {
  hex_terrain: { icon: "⛰️", border: "border-green-600" },
  named_place: { icon: "\u{1F3DB}", border: "border-purple-500" },
  dungeon_room: { icon: "\u{1F6AA}", border: "border-amber-500" },
};

const renderCoords = (node: MapViewNode): string => {
  switch (node.modeProfile) {
    case "hex_terrain":
      return node.coords &&
        node.coords.q !== undefined &&
        node.coords.r !== undefined
        ? `hex · (${node.coords.q},${node.coords.r})`
        : "hex_terrain";
    case "dungeon_room":
      return node.coords?.level !== undefined &&
        node.coords?.roomNumber !== undefined
        ? `dungeon · L${node.coords.level} R${node.coords.roomNumber}`
        : "dungeon_room";
    case "named_place":
      return "named_place";
    default:
      // Exhaustiveness guard: modeProfile is a 3-value enum; cast satisfies compiler if schema extends.
      return `profile_${node.modeProfile as string}`;
  }
};

export const LocationNode = ({ data }: NodeProps<LocationNodeData>) => {
  const theme = MODE_PROFILE_THEME[data.modeProfile];
  const borderClass = data.isPartyLocation
    ? "border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.25)]"
    : theme.border;
  const countClass =
    data.entityCount > 0
      ? "bg-blue-600 text-white"
      : "bg-zinc-600 text-zinc-300";

  return (
    <div
      className={cn(
        "min-w-[120px] rounded-lg border-2 bg-zinc-900 px-3 py-2 text-zinc-100",
        borderClass
      )}
    >
      <Handle className="!bg-zinc-500" position={Position.Top} type="target" />
      <div className="flex items-center gap-1 font-semibold text-xs">
        <span>{theme.icon}</span>
        <span className="truncate">{data.label}</span>
      </div>
      <div className="mt-1 text-[9px] text-zinc-400">{renderCoords(data)}</div>
      <div className="mt-2 flex items-center gap-1">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 font-semibold text-[9px]",
            countClass
          )}
        >
          {data.entityCount}
        </span>
      </div>
      <Handle
        className="!bg-zinc-500"
        position={Position.Bottom}
        type="source"
      />
    </div>
  );
};
